import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Sprout } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import { useAuth } from 'auth/AuthContext';
import ButtonComponent from 'components/common/ButtonComponent';
import SectionIntro from 'components/common/SectionIntro';
import CropModal from 'components/modals/CropModal';
import VarietyModal from 'components/modals/VarietyModal';

const Crops = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState({
    crops: [],
    varieties: [],
    loading: true,
    selectedCrop: null,
    selectedVariety: null,
    isCropModalOpen: false,
    isVarietyModalOpen: false,
    expandedCropId: null
  });

  const fetchCrops = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/crops/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState(s => ({
        ...s,
        crops: Array.isArray(response.data) ? response.data : [],
        loading: false
      }));
    } catch (err) {
      showAlert({ message: t('crops.loadFailed'), icon: 'error' });
      setState(s => ({ ...s, loading: false }));
    }
  };

  const handleSaveCrop = async cropData => {
    const isEdit = !!state.selectedCrop;
    const url = isEdit
      ? `/management/crops/${state.selectedCrop.cropId}`
      : `/management/crops/${user.farmId}`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      await makeRequest({
        method,
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        data: cropData,
        alertResult: true
      });
      fetchCrops();
      setState(s => ({ ...s, isCropModalOpen: false, selectedCrop: null }));
    } catch (err) {
      showAlert({ message: t('crops.saveFailed'), icon: 'error' });
    }
  };

  const handleDeleteCrop = async cropId => {
    const result = await showAlert({
      title: t('crops.deleteCrop'),
      text: t('crops.deleteConfirmation'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common.confirmDelete')
    });

    if (result.isConfirmed) {
      try {
        await makeRequest({
          method: 'DELETE',
          url: `/management/crops/${cropId}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('secret')}`
          },
          alertResult: true
        });
        fetchCrops();
      } catch (err) {
        showAlert({ message: t('crops.deleteFailed'), icon: 'error' });
      }
    }
  };

  const handleSaveVariety = async varietyData => {
    const isEdit = !!state.selectedVariety;
    const url = isEdit
      ? `/management/varieties/${state.selectedVariety.varietyId}`
      : '/management/varieties';
    const method = isEdit ? 'PUT' : 'POST';

    try {
      await makeRequest({
        method,
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        data: varietyData,
        alertResult: true
      });
      fetchCrops();
      setState(s => ({ ...s, isVarietyModalOpen: false, selectedVariety: null }));
    } catch (err) {
      showAlert({ message: t('crops.saveVarietyFailed'), icon: 'error' });
    }
  };

  const handleDeleteVariety = async varietyId => {
    const result = await showAlert({
      title: t('crops.deleteVariety'),
      text: t('crops.deleteVarietyConfirmation'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common.confirmDelete')
    });

    if (result.isConfirmed) {
      try {
        await makeRequest({
          method: 'DELETE',
          url: `/management/varieties/${varietyId}`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('secret')}`
          },
          alertResult: true
        });
        fetchCrops();
      } catch (err) {
        showAlert({ message: t('crops.deleteVarietyFailed'), icon: 'error' });
      }
    }
  };

  const toggleCropExpansion = cropId => {
    setState(s => ({
      ...s,
      expandedCropId: s.expandedCropId === cropId ? null : cropId
    }));
  };

  useEffect(() => {
    if (user?.farmId) {
      fetchCrops();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.farmId]);

  if (state.loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="workspace-crops-loading text-lg">{t('crops.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('crops.title')}
        description={t('pageIntro.cropsDescription')}
        actions={
          <ButtonComponent
            onClick={() => setState(s => ({ ...s, isCropModalOpen: true, selectedCrop: null }))}
          >
            <PlusIcon className="w-5 h-5" />
            <span className="font-medium">{t('crops.newCrop')}</span>
          </ButtonComponent>
        }
        className="mb-6"
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      {/* Crops List */}
      <div className="space-y-3">
        {state.crops.length === 0 ? (
          <div className="workspace-crops-empty rounded-md border border-dashed py-12 text-center">
            <Sprout className="workspace-crops-empty-icon mx-auto mb-4 h-16 w-16" />
            <p className="workspace-crops-empty-title text-lg">{t('crops.noCropsFound')}</p>
            <p className="workspace-crops-empty-text mt-2 text-sm">{t('crops.getStarted')}</p>
          </div>
        ) : (
          state.crops.map(crop => (
            <div
              key={crop.cropId}
              className="workspace-crop-card overflow-hidden rounded-md border"
            >
              {/* Crop Header */}
              <div className="workspace-crop-header flex items-center justify-between border-b p-4">
                <div
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => toggleCropExpansion(crop.cropId)}
                >
                  <Sprout className="workspace-crop-icon w-6 h-6" />
                  <div>
                    <h3 className="workspace-crop-title text-lg font-semibold">{crop.name}</h3>
                    <p className="workspace-crop-meta text-sm">
                      {crop.varieties?.length || 0}{' '}
                      {crop.varieties?.length === 1 ? 'variety' : 'varieties'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setState(s => ({
                        ...s,
                        isVarietyModalOpen: true,
                        selectedVariety: null,
                        selectedCrop: crop
                      }))
                    }
                    className="workspace-crop-action workspace-crop-action--add rounded-md p-2 transition-colors"
                    title="Add Variety"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() =>
                      setState(s => ({ ...s, isCropModalOpen: true, selectedCrop: crop }))
                    }
                    className="workspace-crop-action workspace-crop-action--edit rounded-md p-2 transition-colors"
                    title="Edit Crop"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCrop(crop.cropId)}
                    className="workspace-crop-action workspace-crop-action--delete rounded-md p-2 transition-colors"
                    title="Delete Crop"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Varieties List */}
              {state.expandedCropId === crop.cropId && (
                <div className="workspace-varieties-panel border-t p-4">
                  {crop.varieties && crop.varieties.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {crop.varieties.map(variety => (
                        <div
                          key={variety.varietyId}
                          className="workspace-variety-card flex items-center justify-between rounded-md border p-3 transition-shadow"
                        >
                          <span className="workspace-variety-name font-medium">{variety.name}</span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() =>
                                setState(s => ({
                                  ...s,
                                  isVarietyModalOpen: true,
                                  selectedVariety: variety,
                                  selectedCrop: crop
                                }))
                              }
                              className="workspace-variety-action workspace-variety-action--edit rounded p-1.5 transition-colors"
                              title="Edit Variety"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteVariety(variety.varietyId)}
                              className="workspace-variety-action workspace-variety-action--delete rounded p-1.5 transition-colors"
                              title="Delete Variety"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="workspace-variety-empty py-8 text-center">
                      <p>No varieties yet</p>
                      <button
                        onClick={() =>
                          setState(s => ({
                            ...s,
                            isVarietyModalOpen: true,
                            selectedVariety: null,
                            selectedCrop: crop
                          }))
                        }
                        className="workspace-variety-empty-action mt-2 text-sm hover:underline"
                      >
                        Add first variety
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      <CropModal
        isOpen={state.isCropModalOpen}
        onClose={() => setState(s => ({ ...s, isCropModalOpen: false, selectedCrop: null }))}
        onSave={handleSaveCrop}
        crop={state.selectedCrop}
      />

      <VarietyModal
        isOpen={state.isVarietyModalOpen}
        onClose={() =>
          setState(s => ({
            ...s,
            isVarietyModalOpen: false,
            selectedVariety: null,
            selectedCrop: null
          }))
        }
        onSave={handleSaveVariety}
        variety={state.selectedVariety}
        selectedCrop={state.selectedCrop}
      />
    </div>
  );
};

export default Crops;
